import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, TrendingUpIcon, WalletIcon, ArrowRightIcon, StarIcon } from 'lucide-react';
export function Hero() {
  const [revenue, setRevenue] = useState(0);
  const [profit, setProfit] = useState(0);
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setRevenue(Math.floor(easeOut * 450000));
      setProfit(Math.floor(easeOut * 185000));
      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepTime);
    return () => clearInterval(timer);
  }, []);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(value);
  };
  const scrollToPricing = () => {
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center pt-16 md:pt-24 pb-10 md:pb-16 overflow-hidden" data-id="element-90">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[400px] md:h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" data-id="element-91" />

      <div className="container mx-auto px-5 md:px-6 relative z-10" data-id="element-92">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-8 items-center" data-id="element-93">
          {/* Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="max-w-2xl" data-id="element-94">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface border border-surface-light mb-5 md:mb-8" data-id="element-95">
              <span className="text-base md:text-xl" data-id="element-96">🔥</span>
              <span className="text-[11px] md:text-sm font-medium tracking-wide text-primary-light uppercase" data-id="element-97">
                Oferta Especial de Lanzamiento
              </span>
            </div>

            <h1 className="text-[2rem] leading-[1.1] sm:text-4xl md:text-5xl lg:text-7xl mb-4 md:mb-6 text-balance" data-id="element-98">
              ¿Tenés un negocio, trabajás todo el día y aún así{' '}
              <span className="text-primary italic" data-id="element-99">
                no sabés dónde se va tu dinero?
              </span>
            </h1>

            <p className="text-base md:text-xl text-text-muted mb-6 md:mb-8 leading-relaxed" data-id="element-100">
              El sistema práctico que ayuda a emprendedores y dueños de negocios
              a recuperar el control de sus finanzas, organizar sus números y
              transformar ingresos desordenados en ganancias reales.
            </p>

            <div className="space-y-2 md:space-y-3 mb-6 md:mb-10" data-id="element-101">
              <p className="text-[11px] md:text-sm font-semibold text-text-muted uppercase tracking-wider mb-3 md:mb-4" data-id="element-102">
                Más de 15 recursos incluidos
              </p>
              {['Método de 6 Fases', '6 Bonus Exclusivos', '3 Bonus Plus Premium'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.4 + i * 0.1
            }} className="flex items-center gap-3" data-id="element-103">
                  <CheckCircle2Icon className="w-5 h-5 text-primary flex-shrink-0" data-id="element-104" />
                  <span className="text-base md:text-lg" data-id="element-105">{item}</span>
                </motion.div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-6 mb-6 md:mb-10" data-id="element-106">
              <div className="flex flex-col" data-id="element-107">
                <span className="text-text-muted line-through text-sm md:text-lg" data-id="element-108">
                  $59.900 ARS
                </span>
                <span className="text-2xl md:text-3xl font-bold text-white tabular-nums" data-id="element-109">
                  Desde $19.990 ARS
                </span>
              </div>
              <button onClick={scrollToPricing} className="group relative inline-flex items-center justify-center gap-2 md:gap-3 px-5 md:px-8 py-3.5 md:py-4 bg-primary hover:bg-primary-hover text-text-dark font-bold text-sm md:text-base rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_-5px_rgba(16,185,129,0.4)] w-full sm:w-auto" data-id="element-110">
                QUIERO RECUPERAR EL CONTROL
                <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" data-id="element-111" />
              </button>
            </div>

            <div className="flex items-center gap-3 md:gap-4" data-id="element-112">
              <div className="flex" data-id="element-113">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 md:w-5 md:h-5 fill-gold text-gold" data-id="element-114" />)}
              </div>
              <p className="text-xs md:text-sm text-text-muted" data-id="element-115">
                Más de{' '}
                <strong className="text-white" data-id="element-116">1.000 emprendedores</strong> ya
                comenzaron a tomar el control.
              </p>
            </div>
          </motion.div>

          {/* Dashboard Mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: 'easeOut'
        }} className="relative lg:ml-auto w-full max-w-md mx-auto" data-id="element-117">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent rounded-3xl blur-2xl" data-id="element-118" />

            <div className="relative bg-surface border border-surface-light rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-2xl backdrop-blur-sm" data-id="element-119">
              <div className="flex items-center justify-between mb-5 md:mb-8" data-id="element-120">
                <h3 className="font-sans font-semibold text-base md:text-lg" data-id="element-121">
                  Panel Financiero
                </h3>
                <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20" data-id="element-122">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" data-id="element-123" />
                  <span className="text-[10px] md:text-xs font-medium text-primary-light" data-id="element-124">
                    Organizadas
                  </span>
                </div>
              </div>

              <div className="space-y-3 md:space-y-4" data-id="element-125">
                <div className="bg-background rounded-xl md:rounded-2xl p-4 md:p-5 border border-surface-light" data-id="element-126">
                  <div className="flex items-center gap-2 md:gap-3 mb-2 text-text-muted" data-id="element-127">
                    <WalletIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-128" />
                    <span className="text-xs md:text-sm font-medium" data-id="element-129">
                      Ingresos
                    </span>
                  </div>
                  <div className="flex items-end justify-between" data-id="element-130">
                    <span className="text-xl md:text-3xl font-bold tabular-nums" data-id="element-131">
                      {formatCurrency(revenue)}
                    </span>
                    <div className="flex items-center gap-1 text-primary text-[11px] md:text-sm font-medium bg-primary/10 px-2 py-0.5 md:py-1 rounded-lg" data-id="element-132">
                      <TrendingUpIcon className="w-3 h-3 md:w-4 md:h-4" data-id="element-133" />
                      +12%
                    </div>
                  </div>
                  <div className="mt-3 md:mt-4 h-8 md:h-10 flex items-end gap-1 md:gap-1.5" data-id="element-134">
                    {[40, 30, 50, 45, 70, 65, 85, 100].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} animate={{
                    height: `${h}%`
                  }} transition={{
                    delay: 0.5 + i * 0.1,
                    duration: 0.5
                  }} className="flex-1 bg-surface-light rounded-t-sm relative overflow-hidden" data-id="element-135">
                        <div className="absolute bottom-0 left-0 right-0 bg-primary/40 h-full origin-bottom" style={{
                      opacity: h / 100
                    }} data-id="element-136" />
                      </motion.div>)}
                  </div>
                </div>

                <div className="bg-background rounded-xl md:rounded-2xl p-4 md:p-5 border border-surface-light" data-id="element-137">
                  <div className="flex items-center gap-2 md:gap-3 mb-2 text-text-muted" data-id="element-138">
                    <TrendingUpIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-139" />
                    <span className="text-xs md:text-sm font-medium" data-id="element-140">
                      Ganancia Neta
                    </span>
                  </div>
                  <div className="flex items-end justify-between" data-id="element-141">
                    <span className="text-xl md:text-3xl font-bold tabular-nums text-primary-light" data-id="element-142">
                      {formatCurrency(profit)}
                    </span>
                    <div className="flex items-center gap-1 text-primary text-[11px] md:text-sm font-medium bg-primary/10 px-2 py-0.5 md:py-1 rounded-lg" data-id="element-143">
                      <TrendingUpIcon className="w-3 h-3 md:w-4 md:h-4" data-id="element-144" />
                      +8%
                    </div>
                  </div>
                  <div className="mt-3 md:mt-4 h-2 bg-surface-light rounded-full overflow-hidden" data-id="element-145">
                    <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '68%'
                  }} transition={{
                    delay: 1,
                    duration: 1,
                    ease: 'easeOut'
                  }} className="h-full bg-gradient-to-r from-primary-dark to-primary rounded-full" data-id="element-146" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}